import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import { Switch } from "@project/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import type {
  DonorInfo,
  PaymentInfo,
} from "@project/donate/components/donor-types";
import {
  formatAadhaarDisplay,
  sanitizeAadhaar,
  sanitizePan,
  sanitizePincode,
  type DonorFieldErrors,
} from "@project/donate/components/donor-validation";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { cn } from "@project/lib/utils";

function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}

type DonorFieldsProps = {
  value: DonorInfo;
  onChange: (v: DonorInfo) => void;
  errors?: DonorFieldErrors;
  requireEmail?: boolean;
  requireAddress?: boolean;
  requirePincode?: boolean;
  requirePan?: boolean;
  lockMobile?: boolean;
};

export function DonorFields({
  value,
  onChange,
  errors = {},
  requireEmail = false,
  requireAddress = false,
  requirePincode = false,
  requirePan = false,
  lockMobile = false,
}: DonorFieldsProps) {
  const set = (k: keyof DonorInfo, v: string | boolean) =>
    onChange({ ...value, [k]: v });
  const invalid = (field: keyof DonorInfo) =>
    errors[field] ? "border-destructive focus-visible:ring-destructive" : "";

  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div className="md:col-span-2">
        <Label data-required="true">Full Name</Label>
        <Input
          value={value.name}
          onChange={e => set("name", e.target.value)}
          className={invalid("name")}
        />
        <FieldError message={errors.name} />
      </div>
      <div>
        <Label>Family Name</Label>
        <Input
          value={value.family_name}
          onChange={e => set("family_name", e.target.value)}
        />
      </div>
      <div>
        <Label>Date of Birth</Label>
        <Input
          type="date"
          value={value.date_of_birth}
          onChange={e => set("date_of_birth", e.target.value)}
        />
      </div>
      <div>
        <Label data-required="true">Mobile</Label>
        <MobileNumberField
          value={value.mobile}
          country={value.mobile_country}
          onCountryChange={code => set("mobile_country", code)}
          onChange={mobile => set("mobile", mobile)}
          placeholder="Mobile number"
          disabled={lockMobile}
          inputClassName={invalid("mobile")}
        />
        <FieldError message={errors.mobile} />
      </div>
      <div>
        <Label>Alt Mobile</Label>
        <MobileNumberField
          value={value.alt_mobile}
          country={value.alt_mobile_country}
          onCountryChange={code => set("alt_mobile_country", code)}
          onChange={mobile => set("alt_mobile", mobile)}
          placeholder="Alternate mobile"
          inputClassName={invalid("alt_mobile")}
        />
        <FieldError message={errors.alt_mobile} />
      </div>
      <div className="md:col-span-2">
        <Label data-required={requireEmail ? "true" : undefined}>Email</Label>
        <Input
          type="email"
          value={value.email}
          onChange={e => set("email", e.target.value)}
          placeholder="name@example.com"
          className={invalid("email")}
        />
        <FieldError message={errors.email} />
      </div>
      <div>
        <Label data-required={requirePan ? "true" : undefined}>
          PAN (for 80G)
        </Label>
        <Input
          value={value.pan}
          onChange={e => set("pan", sanitizePan(e.target.value))}
          placeholder="ABCDE1234F"
          maxLength={10}
          className={cn("uppercase", invalid("pan"))}
        />
        <FieldError message={errors.pan} />
      </div>
      <div>
        <Label>Aadhaar</Label>
        <Input
          value={formatAadhaarDisplay(value.aadhaar)}
          onChange={e => set("aadhaar", sanitizeAadhaar(e.target.value))}
          placeholder="XXXX XXXX XXXX"
          inputMode="numeric"
          maxLength={14}
          className={invalid("aadhaar")}
        />
        <FieldError message={errors.aadhaar} />
      </div>
      <div className="md:col-span-2">
        <Label data-required={requireAddress ? "true" : undefined}>
          Address
        </Label>
        <Textarea
          rows={2}
          value={value.address}
          onChange={e => set("address", e.target.value)}
          className={invalid("address")}
        />
        <FieldError message={errors.address} />
      </div>
      <div>
        <Label data-required={requirePincode ? "true" : undefined}>
          Pincode
        </Label>
        <Input
          value={value.pincode}
          onChange={e => set("pincode", sanitizePincode(e.target.value))}
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit pincode"
          className={invalid("pincode")}
        />
        <FieldError message={errors.pincode} />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5">
        <Label htmlFor="csr_eligibility" className="cursor-pointer">
          CSR Eligibility
        </Label>
        <Switch
          id="csr_eligibility"
          checked={value.csr_eligibility}
          onCheckedChange={checked => set("csr_eligibility", checked)}
        />
      </div>
      <div>
        <Label>Occupation</Label>
        <Input
          value={value.occupation}
          onChange={e => set("occupation", e.target.value)}
        />
      </div>
      <div>
        <Label>Company / Firm</Label>
        <Input
          value={value.company}
          onChange={e => set("company", e.target.value)}
        />
      </div>
    </div>
  );
}

export function PaymentFields({
  value,
  onChange,
}: {
  value: PaymentInfo;
  onChange: (v: PaymentInfo) => void;
}) {
  const set = (k: keyof PaymentInfo, v: string) =>
    onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div>
        <Label>Preferred Payment Mode</Label>
        <Select
          value={value.payment_mode}
          onValueChange={v => set("payment_mode", v)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Cash">Cash</SelectItem>
            <SelectItem value="UPI">UPI</SelectItem>
            <SelectItem value="Online">Online (Net Banking / Card)</SelectItem>
            <SelectItem value="Cheque">Cheque</SelectItem>
            <SelectItem value="Bank Transfer">
              Bank Transfer (NEFT/RTGS/IMPS)
            </SelectItem>
            <SelectItem value="Demand Draft">Demand Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Transaction / UTR ID</Label>
        <Input
          value={value.transaction_id}
          onChange={e => set("transaction_id", e.target.value)}
          placeholder="If already paid"
        />
      </div>
      <div>
        <Label>Cheque / DD No.</Label>
        <Input
          value={value.cheque_no}
          onChange={e => set("cheque_no", e.target.value)}
        />
      </div>
      <div>
        <Label>Bank Name</Label>
        <Input
          value={value.bank_name}
          onChange={e => set("bank_name", e.target.value)}
        />
      </div>
    </div>
  );
}
